import { provideHttpClient } from '@angular/common/http';
import { importProvidersFrom, inject, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { PreloadAllModules, provideRouter, RouteReuseStrategy, withPreloading } from '@angular/router';
import { VirtualScrollerModule } from '@iharbeck/ngx-virtual-scroller';
import { NgxParticlesModule } from '@tsparticles/angular';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';
import { provideEnvironmentNgxMask } from 'ngx-mask';
import { ToastrModule } from 'ngx-toastr';
import { AppComponent } from '@app/app.component';
import { routes } from '@app/app.routes';
import { CryptoService } from '@app/store/crypto.service';
import { StoreService } from '@app/store/store.service';
import { AffiliatesService } from '@features/affiliates/affiliates.service';
import { CaseBattleService } from '@features/case-battles/case-battles.service';
import { CaseOpenService } from '@features/case-open/case-open.service';
import { ChatService } from '@features/chat/chat.service';
import { CoinflipService } from '@features/coinflip/coinflip.service';
import { HomeService } from '@features/home/home.service';
import { JackpotService } from '@features/jackpot/jackpot.service';
import { KingService } from '@features/king/king.service';
import { LeaderboardService } from '@features/leaderboard/leaderboard.service';
import { MinesService } from '@features/mines/mines.service';
import { PlinkoService } from '@features/plinko/plinko.service';
import { RafflesService } from '@features/raffles/raffles.service';
import { RewardsService } from '@features/rewards/rewards.service';
import { SupplyDropService } from '@features/supply-drop/supply-drop.service';
import { TowersService } from '@features/towers/towers.service';
import { SessionService } from '@shared/session.service';
import { SocketService } from '@shared/socket.service';
import { DefaultRouteReuseStrategy } from '@shared/strategies/default-route-reuse.strategy';
import { UIService } from '@shared/ui.service';

bootstrapApplication(AppComponent, {
  providers: [
    provideZoneChangeDetection(),
    provideRouter(routes, withPreloading(PreloadAllModules)),
    // Only NgModules that actually contribute root providers belong here — standalone
    // components import their own declarations. NgxParticlesModule provides NgParticlesService
    // and VirtualScrollerModule its default scroll options.
    importProvidersFrom(
      NgxParticlesModule,
      ToastrModule.forRoot({
        positionClass: 'toastr-top-center',
        messageClass: 'toastr-message',
        titleClass: 'toastr-title',
        closeButton: true,
        progressBar: true,
        maxOpened: 4,
        autoDismiss: true,
        newestOnTop: false,
      }),
      VirtualScrollerModule,
    ),
    provideHttpClient(),
    // The BE pushes connect-scoped config exactly once per connection (~800ms after connect)
    // with no request event to ask for it again (`open_plinko` is dead on the BE, unlike
    // `open_battles`). Every socket-consuming service is therefore instantiated here, before
    // first render, so its listeners exist when that burst lands — a service created on first
    // route visit would miss it. Add any new socket-listening service to this list.
    provideAppInitializer(() => {
      inject(SocketService);
      inject(SessionService);
      inject(UIService);
      inject(ChatService);
      inject(SupplyDropService);
      inject(PlinkoService);
      inject(MinesService);
      inject(TowersService);
      inject(CoinflipService);
      inject(KingService);
      inject(JackpotService);
      inject(CaseBattleService);
      inject(CaseOpenService);
      inject(HomeService);
      inject(LeaderboardService);
      inject(RafflesService);
      inject(RewardsService);
      inject(AffiliatesService);
      inject(StoreService);
      inject(CryptoService);
    }),
    {
      provide: RouteReuseStrategy,
      useClass: DefaultRouteReuseStrategy,
    },
    provideCharts(withDefaultRegisterables()),
    provideEnvironmentNgxMask({
      dropSpecialCharacters: true,
    }),
    provideAnimations(),
  ],
}).catch((err) => console.error(err));
